import {
  Button,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { FeePostReviewBasisTabProps } from "features/fees/post/FeePostReviewBasisTabProps";
import {
  useFeePeriodHistoricalTrueUpBreakdownRetrieveSuspense,
  useFeePeriodOffsetsTrueUpBreakdownRetrieveSuspense,
  useFeePeriodsAffectedByTrueUpRetrieveSuspense,
  useFeeTrueUpHistoricalPreviewStatsRetrieveSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import {
  useFeeTrueUpEventCreate,
  useFeeTrueUpEventListSuspense,
  useFundFamilySubEventList,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { CeleryWorkflowStatus } from "gen/model/celeryWorkflowStatus";
import { FundFamilyEventTypeEnum } from "gen/model/fundFamilyEventTypeEnum";
import { FundFamilySubEventTypeEnum } from "gen/model/fundFamilySubEventTypeEnum";
import { PreviousQuarterFeeTrueUpHistoricalStatsAPIData } from "gen/model/previousQuarterFeeTrueUpHistoricalStatsAPIData";
import { isEmpty } from "lodash-es";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { SelectButton } from "shared/components/input/select/SelectButton";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { PageInstructions } from "shared/components/layout/PageInstructions";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { StatTooltip } from "shared/components/stat/StatTooltip";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const HistoricalTrueUpTableStats: React.FC<{
  statsData: PreviousQuarterFeeTrueUpHistoricalStatsAPIData;
}> = ({ statsData }) => {
  const { t } = useTranslation(["fees", "common"]);
  const trueUpHasAdjustment = !decimalOrZero(
    statsData.previous_quarter_fee_true_up_adjustment_amount,
  ).isZero();
  const includingAdjustmentsSuffix = t("common:spaceAndParentheses", {
    text: t("common:includingAdjustments"),
  });
  const excludingAdjustmentsSuffix = t("common:spaceAndParentheses", {
    text: t("common:excludingAdjustments"),
  });
  return (
    <TableBoxStats>
      <StatGroup justifyContent="flex-start">
        {statsData.previous_quarter_rich_name && (
          <>
            <StatTooltip
              label={t(
                "fees:postFees.tabs.historicalBasisTrueUp.tooltips.previousChargedFeesStat",
              )}
            >
              <Stat>
                <StatLabel>
                  {t(
                    "fees:postFees.tabs.historicalBasisTrueUp.originalCalculatedFees",
                    {
                      date: statsData.previous_quarter_rich_name,
                      suffix: excludingAdjustmentsSuffix,
                    },
                  )}
                </StatLabel>
                <StatNumber>
                  {formatMoney(
                    statsData.previous_quarter_charged_fees_excluding_adjustments,
                  )}
                </StatNumber>
              </Stat>
            </StatTooltip>
            <StatTooltip
              label={t(
                "fees:postFees.tabs.historicalBasisTrueUp.tooltips.previousCalculatedFeesStat",
              )}
            >
              <Stat>
                <StatLabel>
                  {t(
                    "fees:postFees.tabs.historicalBasisTrueUp.recalculatedFees",
                    {
                      date: statsData.previous_quarter_rich_name,
                      suffix: excludingAdjustmentsSuffix,
                    },
                  )}
                </StatLabel>
                <StatNumber>
                  {formatMoney(
                    statsData.previous_quarter_calculated_fees_excluding_adjustments,
                  )}
                </StatNumber>
              </Stat>
            </StatTooltip>
            <StatTooltip
              label={t(
                "fees:postFees.tabs.historicalBasisTrueUp.tooltips.trueUpStat",
              )}
            >
              <Stat>
                <StatLabel>
                  {t("fees:postFees.tabs.historicalBasisTrueUp.trueUpAmount", {
                    date: statsData.previous_quarter_rich_name,
                    suffix: excludingAdjustmentsSuffix,
                  })}
                </StatLabel>
                <StatNumber>
                  {formatMoney(
                    statsData.previous_quarter_fee_true_up_amount_excluding_adjustments,
                  )}
                </StatNumber>
              </Stat>
            </StatTooltip>
            {trueUpHasAdjustment && (
              <>
                <StatTooltip
                  label={t(
                    "fees:postFees.tabs.historicalBasisTrueUp.tooltips.trueUpStat",
                  )}
                >
                  <Stat>
                    <StatLabel>
                      {t(
                        "fees:postFees.tabs.historicalBasisTrueUp.trueUpAdjustmentAmount",
                        {
                          date: statsData.previous_quarter_rich_name,
                        },
                      )}
                    </StatLabel>
                    <StatNumber>
                      {formatMoney(
                        statsData.previous_quarter_fee_true_up_adjustment_amount,
                      )}
                    </StatNumber>
                  </Stat>
                </StatTooltip>
                <StatTooltip
                  label={t(
                    "fees:postFees.tabs.historicalBasisTrueUp.tooltips.trueUpStat",
                  )}
                >
                  <Stat>
                    <StatLabel>
                      {t(
                        "fees:postFees.tabs.historicalBasisTrueUp.trueUpAmount",
                        {
                          date: statsData.previous_quarter_rich_name,
                          suffix: includingAdjustmentsSuffix,
                        },
                      )}
                    </StatLabel>
                    <StatNumber>
                      {formatMoney(
                        statsData.previous_quarter_fee_true_up_amount_including_adjustments,
                      )}
                    </StatNumber>
                  </Stat>
                </StatTooltip>
              </>
            )}
          </>
        )}
      </StatGroup>
    </TableBoxStats>
  );
};
// TODO add messaging to make clear offsets are being equalized

export const FeePostReviewBasisHistoricalTrueUpTab: React.FC = () => {
  const { t } = useTranslation(["common", "fees"]);

  const { fundFamilyId, feeChargeEventId, fundFamilyEventId } =
    useOutletContext<Required<FeePostReviewBasisTabProps>>();

  const { showToast, showErrorToast } = useToast();

  const { mutateAsync: runFeeTrueUp, isPending: trueUpLoading } =
    useFeeTrueUpEventCreate();

  const [feeChargeEventSelected, setFeeChargeEventSelected] = useState("");
  const { data: statsData, refetch: refetchStats } =
    useFeeTrueUpHistoricalPreviewStatsRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        selected_fee_charge_event_id: feeChargeEventSelected,
      },
    );

  const { data: trueUpEventList, refetch: refetchEvents } =
    useFeeTrueUpEventListSuspense(fundFamilyId, fundFamilyEventId);

  const { data: historicalTrueUpBreakdownData, refetch: refetchTable } =
    useFeePeriodHistoricalTrueUpBreakdownRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        selected_fee_charge_event_id: feeChargeEventSelected,
      },
    );

  const { data: feePeriodSelectData } =
    useFeePeriodsAffectedByTrueUpRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
    );

  const {
    data: historicalTrueUpOffsetsBreakdownData,
    refetch: refetchOffsetsTable,
  } = useFeePeriodOffsetsTrueUpBreakdownRetrieveSuspense(
    fundFamilyId,
    feeChargeEventId,
    {
      posted_fund_family_event_type: FundFamilyEventTypeEnum.charge_fee,
    },
  );
  const { data: fundFamilySubEvents, refetch: refetchFundFamilySubEventList } =
    useFundFamilySubEventList(fundFamilyId, fundFamilyEventId);
  const filteredSubEvents = fundFamilySubEvents?.filter(
    (subEvent) => subEvent.type === FundFamilySubEventTypeEnum.true_up_fee,
  );
  const workflowIds = filteredSubEvents?.map(
    (subEvent) => subEvent.celery_workflow_id as string | undefined,
  );

  const refetchTables = async () => {
    await Promise.all([
      refetchStats(),
      refetchTable(),
      refetchOffsetsTable(),
      refetchEvents(),
    ]);
  };

  const renderToasts = (isError: boolean) => {
    if (isError) {
      showToast({
        message: t("fees:feeTrueUp.celeryToasts.error"),
        variant: "error",
      });
    } else {
      showToast({
        message: t("fees:feeTrueUp.celeryToasts.success"),
        variant: "success",
      });
    }
  };

  const workflowStatuses = useCeleryWorkflowPoll(
    workflowIds?.filter((id) => id !== undefined) as string[],
    renderToasts,
    refetchTables,
    refetchTables,
  );

  const triggerFeeTrueUp = useCallback(async () => {
    try {
      await runFeeTrueUp({ fundFamilyId, fundFamilyEventId });
      await refetchFundFamilySubEventList();
    } catch (e) {
      showErrorToast(e);
    }
  }, [
    fundFamilyEventId,
    fundFamilyId,
    runFeeTrueUp,
    refetchFundFamilySubEventList,
    showErrorToast,
  ]);

  const isLoading =
    trueUpLoading ||
    Array.from(workflowStatuses.values()).some(
      (value: CeleryWorkflowStatus) =>
        value.state === CeleryWorkflowStateMachineFieldsEnum.processing,
    );

  const hasTrueUpAmount = statsData.has_true_up_amount;
  const hasTrueUpEvents = !isEmpty(trueUpEventList);

  const shouldRenderNoTrueUp =
    hasTrueUpEvents && !isLoading && !hasTrueUpAmount;
  const shouldRenderEqualizedOffsetsTable = !isEmpty(
    historicalTrueUpOffsetsBreakdownData.rows.length,
  );

  const tableData = hasTrueUpAmount
    ? historicalTrueUpBreakdownData
    : { columns: [], rows: [] };

  return (
    <PageTabBody>
      {tableData.rows.length ? (
        <PageInstructions>
          {t("fees:postFees.tabs.historicalBasisTrueUp.disclaimer", {
            date: statsData.previous_quarter_rich_name,
          })}
        </PageInstructions>
      ) : null}
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("fees:postFees.tabs.historicalBasisTrueUp.historicalTrueUp")}
          </TableBoxTitle>
          <DataTableBoxActions>
            <SelectButton
              size="sm"
              title={t("fees:trueUpPages.selectQuarter")}
              value={feeChargeEventSelected}
              onChangeValue={(value) => {
                setFeeChargeEventSelected(value);
              }}
              variant="simple-table"
            >
              {feePeriodSelectData.map((element) => (
                <SelectOption value={element.value} key={element.value}>
                  {element.rich_value}
                </SelectOption>
              ))}
            </SelectButton>
            {!shouldRenderNoTrueUp && (
              <Button
                size="xs"
                isDisabled={isLoading}
                onClick={() => triggerFeeTrueUp()}
              >
                {t("common:refresh")}
              </Button>
            )}
          </DataTableBoxActions>
        </TableBoxHeader>
        <HistoricalTrueUpTableStats statsData={statsData} />
        <TableBoxBody>
          <DataTable
            isLoading={isLoading}
            // we want to show an empty table if there is no true up amount
            // however, the table shows walk of basis and is re-used elsewhere -- so we need to check for this on the FE
            data={tableData}
            emptyStateProps={{
              title: shouldRenderNoTrueUp
                ? t(
                    "fees:postFees.tabs.historicalBasisTrueUp.noTrueUpTableTitle",
                  )
                : t("fees:postFees.tabs.historicalBasisTrueUp.emptyTableTitle"),
              description: shouldRenderNoTrueUp
                ? t(
                    "fees:postFees.tabs.historicalBasisTrueUp.noTrueUpTableDescription",
                  )
                : undefined,
              children: (
                <Button onClick={() => triggerFeeTrueUp()}>
                  {shouldRenderNoTrueUp
                    ? t("common:refresh")
                    : t(
                        "fees:postFees.tabs.historicalBasisTrueUp.emptyTableButton",
                      )}
                </Button>
              ),
            }}
          />
        </TableBoxBody>
      </TableBox>
      {shouldRenderEqualizedOffsetsTable && (
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:trueUpPages.equalizedOffsets")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={historicalTrueUpOffsetsBreakdownData} />
          </TableBoxBody>
        </TableBox>
      )}
    </PageTabBody>
  );
};
